/** @jsxImportSource @emotion/react */
import styled from "@emotion/styled/macro";
import * as colors from "../../../styles/colors";

/* ════════════════════════════════════════════════════════════
   OB/GYN Dashboard — Clinical-grade UI Styles
   ════════════════════════════════════════════════════════════ */

/* ─── Dashboard Layout ──────────────────────────────────── */

export const DashboardContainer = styled.div({
    padding: "16px 20px 30px",
    background: colors.gray003,
    minHeight: "calc(100vh - 120px)",
});

/* ─── Section Dividers ──────────────────────────────────── */

export const SectionHeader = styled.div(({ color: borderColor = colors.blue }) => ({
    display: "flex",
    alignItems: "center",
    gap: "10px",
    margin: "20px 0 12px 0",
    paddingBottom: "8px",
    borderBottom: `2px solid ${borderColor}`,
}));

export const SectionIcon = styled.span(({ color: iconColor = colors.blue }) => ({
    width: "28px",
    height: "28px",
    borderRadius: "6px",
    background: iconColor,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#fff",
    fontSize: "14px",
    flexShrink: 0,
}));

export const SectionTitle = styled.h5({
    fontSize: "13px",
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.8px",
    margin: 0,
    color: colors.text01,
});

export const SectionSubtitle = styled.span({
    fontSize: "11px",
    color: colors.gray80,
    marginLeft: "auto",
});

/* ─── Obstetric Summary Card (ANC Info) ─────────────────── */

export const ObstetricSummaryCard = styled.div({
    background: "#fff",
    border: `1px solid ${colors.gray20}`,
    borderRadius: "8px",
    padding: "16px 20px",
    marginBottom: "16px",
    borderLeft: `4px solid ${colors.blue}`,
});

export const SummaryCardHeader = styled.div({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: "12px",
});

export const SummaryCardTitle = styled.h6({
    fontSize: "13px",
    fontWeight: 700,
    color: colors.blue,
    margin: 0,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
});

export const StatusBadge = styled.span(({ status = "active" }) => {
    const statusMap = {
        active: { bg: colors.lightGreen, color: "#0d8912", text: "Active" },
        delivered: { bg: "#e3f2fd", color: "#1565c0", text: "Delivered" },
        transferred: { bg: colors.lightOrange, color: colors.pending, text: "Transferred" },
        not_booked: { bg: colors.gray, color: colors.gray80, text: "Not Booked" },
    };
    const s = statusMap[status] || statusMap.active;
    return {
        fontSize: "10px",
        fontWeight: 700,
        borderRadius: "4px",
        padding: "3px 10px",
        background: s.bg,
        color: s.color,
        textTransform: "uppercase",
        letterSpacing: "0.5px",
    };
});

export const SummaryGrid = styled.div({
    display: "grid",
    gridTemplateColumns: "repeat(6, 1fr)",
    gap: "0",
    "@media (max-width: 1200px)": {
        gridTemplateColumns: "repeat(3, 1fr)",
    },
    "@media (max-width: 768px)": {
        gridTemplateColumns: "repeat(2, 1fr)",
    },
});

export const SummaryItem = styled.div(({ highlight = false }) => ({
    padding: "8px 12px",
    borderRight: `1px solid ${colors.gray20}`,
    "&:last-child": {
        borderRight: "none",
    },
    background: highlight ? "rgba(56,113,183,0.04)" : "transparent",
}));

export const SummaryLabel = styled.div({
    fontSize: "10px",
    color: colors.gray80,
    textTransform: "uppercase",
    letterSpacing: "0.4px",
    marginBottom: "2px",
});

export const SummaryValue = styled.div(({ size = "normal", color: textColor }) => ({
    fontSize: size === "large" ? "18px" : "13px",
    fontWeight: size === "large" ? 700 : 600,
    color: textColor || colors.text,
    lineHeight: 1.3,
}));

/* ─── Quick Actions Toolbar ─────────────────────────────── */

export const QuickActionsBar = styled.div({
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(110px, 1fr))",
    gap: "8px",
    marginBottom: "16px",
});

export const QuickActionButton = styled.div(({ active = false }) => ({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "6px",
    padding: "12px 8px",
    background: active ? colors.blue : "#fff",
    border: `1px solid ${active ? colors.blue : colors.gray20}`,
    borderRadius: "8px",
    cursor: "pointer",
    transition: "all 0.15s ease",
    "&:hover": {
        borderColor: colors.blue,
        background: active ? colors.blue : "rgba(56,113,183,0.04)",
        transform: "translateY(-1px)",
        boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
    },
}));

export const QuickActionIcon = styled.div(({ active = false, iconColor = colors.blue }) => ({
    width: "32px",
    height: "32px",
    borderRadius: "8px",
    background: active ? "rgba(255,255,255,0.2)" : `${iconColor}15`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    "& svg": {
        width: "18px",
        height: "18px",
        fill: active ? "#fff" : iconColor,
    },
}));

export const QuickActionLabel = styled.span(({ active = false }) => ({
    fontSize: "11px",
    fontWeight: 600,
    color: active ? "#fff" : colors.text01,
    textAlign: "center",
    lineHeight: 1.2,
}));

/* ─── Program Cards (Obstetrics & Gynaecology workflows) ── */

export const ProgramsRow = styled.div({
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "16px",
    "@media (max-width: 992px)": {
        gridTemplateColumns: "1fr",
    },
});

export const ProgramPanel = styled.div(({ borderColor = colors.blue }) => ({
    background: "#fff",
    border: `1px solid ${colors.gray20}`,
    borderRadius: "8px",
    borderTop: `3px solid ${borderColor}`,
    overflow: "hidden",
}));

export const ProgramPanelHeader = styled.div({
    padding: "12px 16px",
    borderBottom: `1px solid ${colors.gray20}`,
    display: "flex",
    alignItems: "center",
    gap: "10px",
});

export const ProgramPanelTitle = styled.h6(({ color: titleColor = colors.blue }) => ({
    fontSize: "13px",
    fontWeight: 700,
    color: titleColor,
    margin: 0,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
}));

export const ProgramPanelBody = styled.div({
    padding: "4px 0",
});

export const WorkflowItem = styled.div(({ available = true, active = false }) => ({
    display: "flex",
    alignItems: "center",
    gap: "12px",
    padding: "10px 16px",
    cursor: available ? "pointer" : "default",
    opacity: available ? 1 : 0.45,
    background: active ? "rgba(56,113,183,0.06)" : "transparent",
    borderLeft: active ? `3px solid ${colors.blue}` : "3px solid transparent",
    transition: "all 0.15s ease",
    "&:hover": available
        ? {
              background: "rgba(56,113,183,0.04)",
          }
        : {},
}));

export const WorkflowStepNumber = styled.div(({ available = true, done = false, color: stepColor = colors.blue }) => ({
    width: "24px",
    height: "24px",
    borderRadius: "50%",
    border: done ? "none" : `2px solid ${available ? stepColor : colors.gray00}`,
    background: done ? stepColor : "transparent",
    color: done ? "#fff" : available ? stepColor : colors.gray00,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "11px",
    fontWeight: 700,
    flexShrink: 0,
}));

export const WorkflowItemText = styled.div({
    flex: 1,
});

export const WorkflowItemTitle = styled.div(({ available = true }) => ({
    fontSize: "13px",
    fontWeight: 500,
    color: available ? colors.text : colors.gray80,
    lineHeight: 1.3,
}));

export const WorkflowItemDesc = styled.div({
    fontSize: "11px",
    color: colors.gray80,
    marginTop: "1px",
});

export const WorkflowItemBadge = styled.span(({ variant = "default" }) => {
    const variants = {
        done: { bg: colors.lightGreen, color: "#0d8912" },
        active: { bg: "#e3f2fd", color: "#1565c0" },
        upcoming: { bg: colors.gray, color: colors.gray80 },
        default: { bg: colors.gray, color: colors.gray80 },
    };
    const v = variants[variant] || variants.default;
    return {
        fontSize: "10px",
        fontWeight: 600,
        borderRadius: "4px",
        padding: "2px 8px",
        background: v.bg,
        color: v.color,
        marginLeft: "auto",
        flexShrink: 0,
    };
});

/* ─── Sidebar Styles (kept for ObGynSidebar.js) ─────────── */

export const SidebarContainer = styled.div({
    height: "100vh",
    overflowY: "auto",
    overflowX: "hidden",
    paddingBottom: "40px",
    background: "#fafbfc",
    "&::-webkit-scrollbar": {
        width: "4px",
    },
    "&::-webkit-scrollbar-thumb": {
        backgroundColor: colors.gray00,
        borderRadius: "10px",
    },
});

export const SidebarSection = styled.div({
    borderBottom: `1px solid ${colors.gray20}`,
});

export const SidebarSectionHeader = styled.div(({ expanded }) => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "10px 14px",
    cursor: "pointer",
    background: expanded ? "rgba(56,113,183,0.05)" : "transparent",
    transition: "background 0.2s",
    "&:hover": {
        background: "rgba(56,113,183,0.07)",
    },
}));

export const SidebarSectionTitle = styled.span(({ color: textColor }) => ({
    fontSize: "11px",
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.7px",
    color: textColor || colors.text01,
}));

export const SidebarItem = styled.div(({ active, disabled }) => ({
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: "9px 14px 9px 24px",
    cursor: disabled ? "default" : "pointer",
    opacity: disabled ? 0.45 : 1,
    fontSize: "12.5px",
    color: active ? colors.blue : colors.text01,
    fontWeight: active ? 600 : 400,
    background: active ? "rgba(56,113,183,0.08)" : "transparent",
    borderRight: active ? `3px solid ${colors.blue}` : "3px solid transparent",
    transition: "all 0.15s ease",
    "&:hover": disabled
        ? {}
        : {
              background: "rgba(56,113,183,0.05)",
              color: colors.blue,
          },
}));

export const SidebarItemBadge = styled.span(({ variant = "default" }) => {
    const variants = {
        active: { bg: colors.lightGreen, color: "#0d8912" },
        count: { bg: colors.lightPurple, color: colors.indigo },
        new: { bg: "#e3f2fd", color: "#1565c0" },
        default: { bg: colors.gray, color: colors.gray80 },
    };
    const v = variants[variant] || variants.default;
    return {
        fontSize: "9px",
        fontWeight: 600,
        borderRadius: "4px",
        padding: "1px 6px",
        background: v.bg,
        color: v.color,
        marginLeft: "auto",
    };
});

export const SidebarDashboardItem = styled.div(({ active }) => ({
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: "12px 14px",
    cursor: "pointer",
    fontSize: "13px",
    fontWeight: 600,
    color: active ? "#fff" : colors.blue,
    background: active
        ? colors.blue
        : "rgba(56,113,183,0.05)",
    borderRight: active ? `3px solid ${colors.blue}` : "3px solid transparent",
    transition: "all 0.15s ease",
    "&:hover": active
        ? {}
        : {
              background: "rgba(56,113,183,0.10)",
          },
}));

export const ChevronIcon = styled.span(({ expanded }) => ({
    display: "inline-flex",
    transition: "transform 0.2s ease",
    transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
    fontSize: "16px",
    color: colors.gray80,
}));

/* ─── No-Booking Call-to-Action ─────────────────────────── */

export const CtaCard = styled.div(({ borderColor = colors.blue }) => ({
    background: "#fff",
    border: `1px solid ${colors.gray20}`,
    borderRadius: "8px",
    padding: "20px 24px",
    marginBottom: "16px",
    borderLeft: `4px solid ${borderColor}`,
    display: "flex",
    alignItems: "center",
    gap: "16px",
}));

export const CtaContent = styled.div({
    flex: 1,
});

export const CtaTitle = styled.h6({
    fontSize: "14px",
    fontWeight: 600,
    color: colors.text,
    margin: "0 0 4px 0",
});

export const CtaDescription = styled.p({
    fontSize: "12px",
    color: colors.gray80,
    margin: 0,
    lineHeight: 1.4,
});

export const CtaButton = styled.button(({ color: btnColor = colors.blue }) => ({
    padding: "8px 20px",
    background: btnColor,
    color: "#fff",
    border: "none",
    borderRadius: "6px",
    fontSize: "12px",
    fontWeight: 600,
    cursor: "pointer",
    whiteSpace: "nowrap",
    transition: "all 0.15s ease",
    "&:hover": {
        opacity: 0.9,
        boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
    },
}));

/* ─── Right Mini Sidebar (Clinical Actions) ─────────────── */

export const MiniSidebarContainer = styled.div({
    height: "100vh",
    overflowY: "auto",
    overflowX: "hidden",
    background: "#fff",
    borderLeft: `1px solid ${colors.gray20}`,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: "8px 0",
    gap: "2px",
    "&::-webkit-scrollbar": {
        width: "3px",
    },
    "&::-webkit-scrollbar-thumb": {
        backgroundColor: colors.gray00,
        borderRadius: "10px",
    },
});

export const MiniSidebarItem = styled.div(({ active = false }) => ({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "3px",
    padding: "8px 4px",
    cursor: "pointer",
    borderRadius: "6px",
    width: "calc(100% - 8px)",
    margin: "0 4px",
    background: active ? "rgba(56,113,183,0.10)" : "transparent",
    transition: "all 0.15s ease",
    "&:hover": {
        background: active ? "rgba(56,113,183,0.10)" : "rgba(56,113,183,0.05)",
    },
    "& svg": {
        width: "20px",
        height: "20px",
        fill: active ? colors.blue : colors.gray80,
    },
}));

export const MiniSidebarLabel = styled.span(({ active = false }) => ({
    fontSize: "9px",
    fontWeight: active ? 600 : 500,
    color: active ? colors.blue : colors.gray80,
    textAlign: "center",
    lineHeight: 1.1,
    letterSpacing: "0.2px",
}));

export const MiniSidebarDivider = styled.div({
    width: "70%",
    height: "1px",
    background: colors.gray20,
    margin: "4px 0",
});

/* ─── Compact Patient Info Strip ─────────────────────────── */

export const PatientStrip = styled.div({
    background: "#fff",
    border: `1px solid ${colors.gray20}`,
    borderRadius: "6px",
    padding: "10px 16px",
    marginBottom: "12px",
    display: "flex",
    alignItems: "center",
    gap: "16px",
    flexWrap: "wrap",
});

export const PatientStripAvatar = styled.div({
    width: "36px",
    height: "36px",
    borderRadius: "50%",
    background: colors.blue,
    color: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "14px",
    fontWeight: 700,
    flexShrink: 0,
});

export const PatientStripName = styled.div({
    fontWeight: 700,
    fontSize: "14px",
    color: colors.text,
    lineHeight: 1.2,
    "& span": {
        display: "block",
        fontSize: "11px",
        fontWeight: 400,
        color: colors.gray80,
    },
});

export const PatientStripDivider = styled.div({
    width: "1px",
    height: "28px",
    background: colors.gray20,
});

export const PatientStripField = styled.div({
    display: "flex",
    flexDirection: "column",
});

export const PatientStripLabel = styled.span({
    fontSize: "10px",
    color: colors.gray80,
    textTransform: "uppercase",
    letterSpacing: "0.3px",
});

export const PatientStripValue = styled.span({
    fontSize: "12.5px",
    fontWeight: 600,
    color: colors.text,
});
